/**
 * useCache — React hook for accessing the CacheManager.
 *
 * Provides pure cache operations (get/set/has/invalidate). The caller
 * is responsible for triggering fetches — this hook only manages the store.
 *
 * Supports two scopes:
 * - 'global' (default): shared across the entire app (countries, states, plans)
 * - 'instance': isolated per hook mount (form-specific ephemeral data)
 *
 * Usage:
 *   const cache = useCache();
 *
 *   const result = cache.get<CountryOption[]>(key);
 *   if (result.status === 'miss') {
 *     const data = await ActionRegistry.executeData('GET_COUNTRIES', {});
 *     cache.set(key, data, IMMUTABLE_POLICY);
 *   }
 */

import { useRef, useCallback, useEffect } from 'react';
import { CacheManager, globalCache } from '../cache/CacheManager';
import type { CacheResult } from '../cache/CacheManager';
import type { CachePolicy } from '../cache/CachePolicy';

interface UseCacheOptions {
  /**
   * 'global' — uses the app-wide singleton (shared across all engines).
   * 'instance' — creates a dedicated cache scoped to this hook's lifetime.
   *              Automatically cleaned up on unmount.
   */
  scope?: 'global' | 'instance';
  /** Max entries for instance-scoped caches. Ignored for global scope. */
  maxEntries?: number;
}

export function useCache(options: UseCacheOptions = {}) {
  const { scope = 'global', maxEntries } = options;
  const instanceRef = useRef<CacheManager | null>(null);

  const getCache = useCallback((): CacheManager => {
    if (scope === 'instance') {
      if (!instanceRef.current) {
        instanceRef.current = new CacheManager({ maxEntries });
      }
      return instanceRef.current;
    }
    return globalCache;
  }, [scope, maxEntries]);

  useEffect(() => {
    return () => {
      if (scope === 'instance' && instanceRef.current) {
        instanceRef.current.destroy();
        instanceRef.current = null;
      }
    };
  }, [scope]);

  const get = useCallback(
    <T = unknown>(key: string): CacheResult<T> => {
      return getCache().get<T>(key);
    },
    [getCache],
  );

  const set = useCallback(
    <T = unknown>(key: string, data: T, policy?: Partial<CachePolicy>): void => {
      getCache().set(key, data, policy);
    },
    [getCache],
  );

  const has = useCallback(
    (key: string): boolean => {
      return getCache().has(key);
    },
    [getCache],
  );

  const invalidate = useCallback(
    (key: string): void => {
      getCache().invalidate(key);
    },
    [getCache],
  );

  const invalidateByPrefix = useCallback(
    (prefix: string): void => {
      getCache().invalidateByPrefix(prefix);
    },
    [getCache],
  );

  return { get, set, has, invalidate, invalidateByPrefix };
}
